import { createB2BShoppingList, createBcShoppingList } from '@/shared/service/b2b';
import { store } from '@/store';

import { b2bPermissionsMap, validatePermissionWithComparisonType } from '../b3CheckPermissions';
import { channelId } from '../basicConfig';
import { itemPrices } from '@/shared/service/b2b/api/global';
import { ShoppingListsItemsProps } from '@/pages/ShoppingLists/config';
import { getB2BShoppingListDetails, getBcShoppingListDetails } from '@/shared/service/b2b';

interface CreateShoppingListParams {
  data: { name: string; description: string };
  isB2BUser: boolean;
}

const createShoppingList = ({
  data,
  isB2BUser,
}: // currentChannelId,
CreateShoppingListParams) => {
  const createShoppingData: Record<string, string | number> = data;

  const createSL = isB2BUser ? createB2BShoppingList : createBcShoppingList;

  if (isB2BUser) {
    const submitShoppingListPermission = validatePermissionWithComparisonType({
      code: b2bPermissionsMap.submitShoppingListPermission,
    });
    const selectCompanyHierarchyId =
      store.getState()?.company?.companyHierarchyInfo?.selectCompanyHierarchyId || 0;
    createShoppingData.status = submitShoppingListPermission ? 30 : 0;
    if (selectCompanyHierarchyId) {
      createShoppingData.companyId = selectCompanyHierarchyId;
    }
  } else {
    createShoppingData.channelId = channelId;
  }

  return createSL({ ...createShoppingData, channelId });
};

export const getCurrentLocalTime = () => {
  return new Date().toLocaleString('en-US', { 
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export const downloadCSV = (data: any, filename: string) => {
  const csvContent = data.map((row: any) => 
    Object.values(row)
      .map(cell => {
        if (cell === null || cell === undefined) {
          return '';
        }
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      })
      .join(',')
  ).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface WorksheetInfo {
  name: string;
  description: string;
  customerName: string;
}

export const generatePricingWorksheetCSV = (worksheetInfo: WorksheetInfo, products: any[]) => {
  const csvData = [];
  csvData.push({
    col1: 'Account Name: ' + worksheetInfo.customerName || ''
  });

  csvData.push({
    col1: 'Account ID: ' + worksheetInfo.description || ''
  });

  csvData.push({
    col1: 'Worksheet Name: ' + worksheetInfo.name || ''
  });

  csvData.push({
    col1: `Pricing and Inventory published is valid as of: ${getCurrentLocalTime()} and is subject to change at any time.`
  });

  csvData.push({});

  csvData.push({
    itemNumber: 'Item Number',
    description: 'Description',
    size: 'Size',
    figureNumber: 'Figure Number',
    inventory: 'Inventory',
    currency: 'Currency',
    priceSheet: 'Effective Price Sheet',
    listPrice: 'List Price',
    discountFactor: 'Discount Factor',
    netPrice: 'Net Price'
  });

  products.forEach((product: any) => {
    const priceData = product.productPriceData || {};

    csvData.push({
      itemNumber: quoteText(product.variantSku || product.baseSku),
      description: product.productName || '',
      size: quoteText(priceData.SIZE),
      figureNumber: quoteText(priceData.FIGURENUMBER),
      inventory: priceData.TOTALAVAILABLEINVENTORY || '',
      currency: priceData.CURRENCY || '',
      priceSheet: Array.isArray(priceData.PRICELISTSHEETNAME) || !priceData.PRICELISTSHEETNAME ? quoteText('-') : priceData.PRICELISTSHEETNAME,
      listPrice: Array.isArray(priceData.LISTPRICE) || !priceData.LISTPRICE ? quoteText('-') : 
                  quoteText(formattedPrice(priceData.LISTPRICE, priceData.CURRENCY)),
      discountFactor: Array.isArray(priceData.DISCOUNTMULTIPLIER) || !priceData.DISCOUNTMULTIPLIER ? quoteText('-') :
                      quoteText(Number(priceData.DISCOUNTMULTIPLIER).toFixed(4)),
      netPrice: Array.isArray(priceData.NETPRICE) || !priceData.NETPRICE ? quoteText('-') :
                quoteText(formattedPrice(priceData.NETPRICE, priceData.CURRENCY))
    });
  });
  
  return csvData;
};

const quoteText = (text: string | number | undefined): string => {
  if (text === undefined || text === null) return '';
  return `="${String(text).replace(/"/g, '""')}"`;
};

export const fetchProductPriceDataForExport = async (accountNumber: string, products: any[]) => {
  const productsWithPriceData = [...products];
  for (let i = 0; i < productsWithPriceData.length; i++) {
    const product = productsWithPriceData[i];
    const node = product.node || product;
    const productSku = node.variantSku || node.baseSku || '';
    
    if (productSku) {
      try {
        const response = await itemPrices(accountNumber, productSku);
        let data;
        try {
          data = JSON.parse(response);
        } catch (e) {
          data = response;
        }

        let priceData = {};
        if (data && 
            data['REPLYERRDATA'] && 
            data['REPLYERRDATA']['RPYERRFLG'] === '0' && 
            data['REPLYDATA'] && 
            data['REPLYDATA']['ITEMS'] && 
            data['REPLYDATA']['ITEMS']['ITEM'] && 
            data['REPLYDATA']['ITEMS']['ITEM'].length > 0) {
          
          priceData = data['REPLYDATA']['ITEMS']['ITEM'][0];
        }
        
        if (node.productPriceData) {
          node.productPriceData = priceData;
        } else {
          node.productPriceData = priceData;
        }
      } catch (error) {
        console.error(`Failed to fetch price data for export for product ${productSku}:`, error);
      }
    }
  }
  
  return productsWithPriceData;
};

export const exportPricingWorksheetToCSV = async (shoppingListInfo: any, products: any, shouldFetchPriceData = true) => {
  try {
    let productsToExport = products;
    let accountNumber = shoppingListInfo?.description || '';
    const customerAccount = localStorage.getItem('profileOptions');
    const customerAccountInfo = customerAccount ? JSON.parse(customerAccount) : null;
    let activeAccountName = '';
    if(customerAccountInfo) {
      activeAccountName = customerAccountInfo.find(
        (item: any) => item.M3_ACCOUNT_NUMBER === shoppingListInfo?.description,
      )?.companyName;
    }
    
    const worksheetInfo = {
      name: shoppingListInfo?.name || '',
      description: accountNumber,
      customerName: activeAccountName || ''
    };

    productsToExport = productsToExport.map((product: any) => {
      if (product.node) {
        return product.node;
      }
      return product;
    });
    if (shouldFetchPriceData) {
      productsToExport = await fetchProductPriceDataForExport(accountNumber, productsToExport);
    }

    const csvData = generatePricingWorksheetCSV(worksheetInfo, productsToExport);
    const filename = `pricing_worksheet_${worksheetInfo.name.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    
    downloadCSV(csvData, filename);
  } catch (error) {
    console.error('Error exporting pricing worksheet:', error);
  }
};

export const exportShoppingList = async (shoppingList: ShoppingListsItemsProps, isB2BUser: boolean) => {
  try {
    const pageSize = 50;
    let options = { id: shoppingList.id, first: pageSize, offset: 0, orderBy: "updatedAt" };
    
    const details = isB2BUser 
      ? await getB2BShoppingListDetails(options) 
      : await getBcShoppingListDetails({ ...options, channelId });

    let allProducts = [...(details.products?.edges || [])];
    const totalCount = details.products?.totalCount || 0;

    const remainingCount = totalCount - pageSize;
    if (remainingCount > 0) {
      const remainingPages = Math.ceil(remainingCount / pageSize);
      
      for (let page = 1; page <= remainingPages; page++) {
        const pageOptions = { 
          id: shoppingList.id, 
          first: pageSize, 
          offset: page * pageSize,
          orderBy: "updatedAt" 
        };
        
        const pageDetails = isB2BUser 
          ? await getB2BShoppingListDetails(pageOptions) 
          : await getBcShoppingListDetails({ ...pageOptions, channelId });
        
        const pageProducts = pageDetails.products?.edges || [];
        allProducts = [...allProducts, ...pageProducts];
      }
    }
    await exportPricingWorksheetToCSV(shoppingList, allProducts, true);
    return true;
  } catch (error) {
    console.error('Error downloading worksheet:', error);
    return false;
  }
}

export const formattedPrice = (price: string, currency: string) => {
  const formatPrice = currency === 'CAD' ? `$C${price}` : `$${price}`;
  return formatPrice;
}

export const formattedPriceASC = (price: string, currency: string) => {
  if(currency === 'null' || currency === 'undefined' || currency === null) {
    currency = '';
  }
  const numPrice = Number(price);
  const isNegative = numPrice < 0;
  const absPrice = Math.abs(numPrice);
  const formattedNumber = absPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPrice = currency === 'CAD' ? `$C${formattedNumber}` : `$${formattedNumber}`;
  return `${isNegative ? '-' : ''}${formatPrice} ${currency}`;
}

export default createShoppingList;
